import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { MapPin, Users, Send, Loader2, GraduationCap, Sparkles, Trophy, ArrowLeft, Plus, Pencil, Trash2, MessageSquareText } from "lucide-react";
import { toast } from "sonner";
import TemplatePicker from "@/components/TemplatePicker";
import { Input } from "@/components/ui/input";

export default function Locations() {
  // Hydrate locations from sessionStorage so this tab opens instantly.
  const cachedLocs = (() => { try { return JSON.parse(sessionStorage.getItem("sts:locations") || "null") || []; } catch { return []; } })();
  const [locations, setLocations] = useState(cachedLocs);
  const [active, setActive] = useState(null);        // location object being viewed
  const [contacts, setContacts] = useState([]);
  const [locClasses, setLocClasses] = useState([]);
  const [subTab, setSubTab] = useState("classes");   // classes | parents
  const [openMsg, setOpenMsg] = useState(false);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [classToText, setClassToText] = useState(null); // set when texting a single class
  const [manageOpen, setManageOpen] = useState(false);
  const [manageMode, setManageMode] = useState("add"); // add | edit
  const [manageForm, setManageForm] = useState({ code: "", label: "", original_code: "" });
  const [manageSaving, setManageSaving] = useState(false);
  const nav = useNavigate();

  const openClassInEditor = (cls) => nav(`/classes?open=${encodeURIComponent(cls.id)}`);
  const deleteClass = async (cls, e) => {
    e?.stopPropagation();
    if (cls.enrolled_count > 0) { toast.error(`Cannot delete — ${cls.enrolled_count} student(s) enrolled. Drop them first.`); return; }
    if (!window.confirm(`Delete class "${cls.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/classes/${cls.id}`);
      toast.success(`Deleted ${cls.name}`);
      // reload the current location so the card disappears
      if (active) openLocation(active);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/locations");
      setLocations(data);
      try { sessionStorage.setItem("sts:locations", JSON.stringify(data)); } catch {}
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const openLocation = async (loc) => {
    setActive(loc);
    setSubTab("classes");
    try {
      const [{ data: cts }, { data: cls }] = await Promise.all([
        api.get(`/locations/${loc.code}/contacts`),
        api.get(`/locations/${loc.code}/classes`),
      ]);
      setContacts(cts);
      setLocClasses(cls);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const closeDetail = () => { setActive(null); setContacts([]); setLocClasses([]); };

  const openAddLocation = () => { setManageMode("add"); setManageForm({ code: "", label: "", original_code: "" }); setManageOpen(true); };
  const openEditLocation = (loc, e) => { e?.stopPropagation(); setManageMode("edit"); setManageForm({ code: loc.code, label: loc.label || loc.code, original_code: loc.code }); setManageOpen(true); };

  const saveLocation = async () => {
    const code = (manageForm.code || "").trim();
    const label = (manageForm.label || "").trim() || code;
    if (!code) { toast.error("Location code required"); return; }
    setManageSaving(true);
    try {
      if (manageMode === "add") {
        await api.post("/locations", { code, label });
        toast.success(`Added ${label}`);
      } else {
        await api.put(`/locations/${encodeURIComponent(manageForm.original_code)}`, { new_code: code, new_label: label });
        toast.success(`Renamed to ${label}`);
      }
      setManageOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setManageSaving(false); }
  };

  const deleteLocation = async (loc, e) => {
    e?.stopPropagation();
    if (loc.class_count > 0) { toast.error(`Move or delete the ${loc.class_count} class(es) at ${loc.label} first`); return; }
    if (!window.confirm(`Delete location "${loc.label}"?`)) return;
    try {
      await api.delete(`/locations/${encodeURIComponent(loc.code)}`);
      toast.success(`Deleted ${loc.label}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openTextLocation = () => { setClassToText(null); setOpenMsg(true); };
  const openTextClass = (cls) => { setClassToText(cls); setOpenMsg(true); };

  const send = async () => {
    if (!body.trim()) return;
    setSending(true);
    try {
      if (classToText) {
        const { data } = await api.post("/messages/broadcast", { class_id: classToText.id, body });
        toast.success(`Sent to ${data.sent_count} parents in ${classToText.name}`);
      } else {
        const { data } = await api.post("/messages/broadcast-location", { location_code: active.code, body });
        toast.success(`Sent to ${data.sent_count} parents at ${active.label}`);
      }
      setOpenMsg(false); setBody(""); setClassToText(null);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  const groupedClasses = () => {
    const groups = { Recreational: [], "Competitive · Elite": [], "Competitive · Company": [], Other: [] };
    for (const c of locClasses) {
      if (c.program === "Recreational") groups.Recreational.push(c);
      else if (c.program === "Competitive" && c.division === "Elite") groups["Competitive · Elite"].push(c);
      else if (c.program === "Competitive" && c.division === "Company") groups["Competitive · Company"].push(c);
      else groups.Other.push(c);
    }
    return Object.entries(groups).filter(([, arr]) => arr.length > 0);
  };

  return (
    <div className="h-full overflow-y-auto thin-scroll" data-testid="locations-page">
      <div className="max-w-6xl mx-auto p-8">
        {!active && (
          <>
            <div className="mb-6 flex items-end justify-between gap-3">
              <div>
                <h1 className="text-3xl font-bold tracking-tight text-slate-900">Locations</h1>
                <p className="text-sm text-slate-500 mt-1">Click a location to browse its classes and text a class or the whole location.</p>
              </div>
              <Button onClick={openAddLocation} className="bg-[#D9232E] hover:bg-[#B91C25] text-white" data-testid="add-location-button">
                <Plus className="w-4 h-4 mr-2" /> Add location
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {locations.map((loc) => (
                <div key={loc.code} className="bg-white border border-slate-200 rounded-lg p-5 hover:shadow-sm hover:border-slate-300 transition-all cursor-pointer group relative" onClick={() => openLocation(loc)} data-testid={`location-card-${loc.code}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-[#D9232E]" />
                    </div>
                    <div className="flex items-center gap-1">
                      <Badge variant="secondary" className="text-[10px] flex items-center gap-1">
                        <Users className="w-3 h-3" /> {loc.student_count}
                      </Badge>
                      <button type="button" onClick={(e) => openEditLocation(loc, e)} className="p-1 text-slate-400 hover:text-[#D9232E] hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`edit-location-${loc.code}`} title="Rename">
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button type="button" onClick={(e) => deleteLocation(loc, e)} disabled={loc.class_count > 0} className="p-1 text-slate-400 hover:text-[#D9232E] hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-opacity disabled:cursor-not-allowed" data-testid={`delete-location-${loc.code}`} title={loc.class_count > 0 ? `${loc.class_count} classes still here` : "Delete location"}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  <h3 className="font-bold text-slate-900 text-[15px] leading-tight">{loc.label}</h3>
                  <div className="text-xs text-slate-500 mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
                    <span>{loc.class_count} classes</span>
                    <span className="text-[#D9232E] font-semibold">View →</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {active && (
          <>
            <div className="flex items-center gap-3 mb-6">
              <Button variant="ghost" size="sm" onClick={closeDetail} className="text-slate-600" data-testid="back-to-locations">
                <ArrowLeft className="w-4 h-4 mr-1" /> Locations
              </Button>
              <div className="flex-1">
                <h1 className="text-3xl font-bold tracking-tight text-slate-900">{active.label}</h1>
                <p className="text-sm text-slate-500 mt-1">{locClasses.length} classes · {contacts.length} unique parents</p>
              </div>
              <Button className="bg-[#D9232E] hover:bg-[#B91C25] text-white" onClick={openTextLocation} disabled={contacts.length === 0} data-testid="text-location-button">
                <Send className="w-4 h-4 mr-2" /> Text Location ({contacts.length})
              </Button>
            </div>

            <Tabs value={subTab} onValueChange={setSubTab} className="mb-4">
              <TabsList className="bg-slate-100">
                <TabsTrigger value="classes" data-testid="loc-tab-classes" className="data-[state=active]:bg-white data-[state=active]:text-[#D9232E]">
                  <GraduationCap className="w-4 h-4 mr-2" /> Classes ({locClasses.length})
                </TabsTrigger>
                <TabsTrigger value="parents" data-testid="loc-tab-parents" className="data-[state=active]:bg-white data-[state=active]:text-[#D9232E]">
                  <Users className="w-4 h-4 mr-2" /> Parents ({contacts.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="classes" className="mt-4">
                {locClasses.length === 0 && (
                  <div className="text-center py-12 text-slate-400 text-sm">No classes at this location.</div>
                )}
                {groupedClasses().map(([groupLabel, arr]) => (
                  <div key={groupLabel} className="mb-6">
                    <div className="flex items-center gap-2 mb-2">
                      {groupLabel.startsWith("Recreational") ? <Sparkles className="w-4 h-4 text-[#D9232E]" /> : <Trophy className="w-4 h-4 text-[#D9232E]" />}
                      <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wide">{groupLabel}</h3>
                      <span className="text-xs text-slate-400">({arr.length})</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {arr.map((cls) => (
                        <div
                          key={cls.id}
                          onClick={() => openClassInEditor(cls)}
                          className="bg-white border border-slate-200 rounded-lg p-4 hover:shadow-sm hover:border-[#D9232E]/40 transition-all cursor-pointer group relative"
                          data-testid={`loc-class-card-${cls.id}`}
                          title="Click to open class roster & edit"
                        >
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <h4 className="font-semibold text-slate-900 text-sm leading-tight">{cls.name}</h4>
                            <div className="flex items-center gap-1 shrink-0">
                              <Badge variant="secondary" className="text-[10px] flex items-center gap-1">
                                <Users className="w-2.5 h-2.5" /> {cls.enrolled_count}
                              </Badge>
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); openClassInEditor(cls); }}
                                className="p-1 text-slate-400 hover:text-[#D9232E] hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                                data-testid={`loc-class-edit-${cls.id}`}
                                title="Edit class"
                              >
                                <Pencil className="w-3 h-3" />
                              </button>
                              <button
                                type="button"
                                onClick={(e) => deleteClass(cls, e)}
                                disabled={cls.enrolled_count > 0}
                                className="p-1 text-slate-400 hover:text-[#D9232E] hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-opacity disabled:cursor-not-allowed"
                                data-testid={`loc-class-delete-${cls.id}`}
                                title={cls.enrolled_count > 0 ? `Drop the ${cls.enrolled_count} student(s) first` : "Delete class"}
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                          <p className="text-[11px] text-slate-500 mb-3">{cls.day || "—"}{cls.time ? ` · ${cls.time}` : ""} {cls.instructor ? `· ${cls.instructor}` : ""}</p>
                          <Button
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); openTextClass(cls); }}
                            disabled={!cls.enrolled_count}
                            className="w-full h-7 bg-[#D9232E] hover:bg-[#B91C25] text-white text-[11px] font-semibold disabled:opacity-40"
                            data-testid={`text-class-btn-${cls.id}`}
                          >
                            <Send className="w-3 h-3 mr-1" /> Text Class
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </TabsContent>

              <TabsContent value="parents" className="mt-4">
                <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                      <tr>
                        <th className="text-left px-4 py-2 font-semibold">Parent</th>
                        <th className="text-left px-4 py-2 font-semibold">Phone</th>
                        <th className="text-left px-4 py-2 font-semibold">Email</th>
                        <th className="w-16"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {contacts.length === 0 && <tr><td colSpan={4} className="text-center py-8 text-slate-400">No parents at this location.</td></tr>}
                      {contacts.map((c) => (
                        <tr key={c.id} className="border-t border-slate-100 group">
                          <td className="px-4 py-2.5 font-medium text-slate-900">{c.first_name} {c.last_name}</td>
                          <td className="px-4 py-2.5 text-slate-600">{c.phone}</td>
                          <td className="px-4 py-2.5 text-slate-600">{c.email || "—"}</td>
                          <td className="px-2 py-2 text-right">
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={async () => {
                                try {
                                  const { data } = await api.post("/conversations/by-contact", { contact_id: c.id });
                                  nav(`/inbox/${data.conversation_id}`);
                                } catch (e) { toast.error(formatApiError(e, "Couldn't open thread")); }
                              }}
                              className="h-7 w-7 opacity-40 group-hover:opacity-100 transition-opacity hover:bg-red-50 hover:text-[#D9232E]"
                              data-testid={`text-parent-${c.id}`}
                              title={`Text ${c.first_name || c.phone}`}
                            >
                              <MessageSquareText className="w-3.5 h-3.5" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </TabsContent>
            </Tabs>
          </>
        )}

        {/* Unified broadcast dialog — used for both "Text Location" and "Text Class" */}
        <Dialog open={openMsg} onOpenChange={(v) => { setOpenMsg(v); if (!v) { setBody(""); setClassToText(null); } }}>
          <DialogContent data-testid="loc-broadcast-dialog">
            <DialogHeader>
              <DialogTitle>
                {classToText ? `Text ${classToText.name}` : `Text everyone at ${active?.label}`}
              </DialogTitle>
              <p className="text-sm text-slate-500">
                {classToText
                  ? `Individual SMS to each of ${classToText.enrolled_count} parents in this class.`
                  : `Individual SMS to each of ${contacts.length} parents at this location.`}
              </p>
            </DialogHeader>
            <div className="flex items-center justify-between">
              <div className="text-xs text-slate-400">
                {body.length} chars · {classToText ? classToText.enrolled_count : contacts.length} recipients
              </div>
              <TemplatePicker onSelect={(b) => setBody(b)} testId="loc-template-picker" />
            </div>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Type your message..." rows={5} data-testid="loc-broadcast-body" />
            <DialogFooter>
              <Button variant="outline" onClick={() => { setOpenMsg(false); setClassToText(null); setBody(""); }}>Cancel</Button>
              <Button onClick={send} disabled={sending || !body.trim()} className="bg-[#D9232E] hover:bg-[#B91C25] text-white" data-testid="loc-broadcast-send">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : `Send to ${classToText ? classToText.enrolled_count : contacts.length}`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {/* Add / Rename location dialog */}
        <Dialog open={manageOpen} onOpenChange={setManageOpen}>
          <DialogContent data-testid="manage-location-dialog">
            <DialogHeader>
              <DialogTitle>{manageMode === "add" ? "Add location" : "Rename location"}</DialogTitle>
              <p className="text-sm text-slate-500">
                {manageMode === "add" ? "Add a new studio location. Assign classes to it via Class → Edit." : "Renaming moves every class currently at this location to the new code/name."}
              </p>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">Code*</label>
                <Input value={manageForm.code} onChange={(e) => setManageForm({ ...manageForm, code: e.target.value })} placeholder="e.g. Victoria" data-testid="manage-location-code" className="mt-1" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">Display name</label>
                <Input value={manageForm.label} onChange={(e) => setManageForm({ ...manageForm, label: e.target.value })} placeholder="Same as code if left blank" data-testid="manage-location-label" className="mt-1" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setManageOpen(false)}>Cancel</Button>
              <Button onClick={saveLocation} disabled={manageSaving || !manageForm.code.trim()} className="bg-[#D9232E] hover:bg-[#B91C25] text-white" data-testid="save-location-button">
                {manageSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : (manageMode === "add" ? "Add" : "Save")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
